//TD title + button styles
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from 'api';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import routes from 'utils/routes';
// components
import { Button } from '@ontruck/orc';
import UsersList from 'components/UsersList';
import UserModal from 'components/UserModal';
import LoadSpinner from 'components/LoadSpinner';
import { selectors as authSelectors } from 'state/auth';
import { actions as ordersActions } from 'state/orders';
import { replace } from 'connected-react-router';
import { userIsAuthenticatedRedir } from 'components/auth/hoc';
import { Title } from 'refactored/ui/typography';
import { Main, GridRow } from 'refactored/ui/layout';
import AuthenticatedUserView from 'refactored/ui/views/authenticatedUserView';
// styles
import styled, { css } from 'styled-components';
import { gridSizes, media } from 'utils/style';

const headerContainerRegularCSS = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
`;
const headerContainerTabletCSS = css`
  display: flex;
  align-items: center;
  button {
    align-self: end;
  }
`;
const headerContainerMobileCSS = css`
  padding: ${gridSizes('M')};
`;

const HeaderContainer = styled.div`
  ${headerContainerRegularCSS}
  ${media.lessThan('medium')`${headerContainerTabletCSS}`}
  ${media.lessThan('small')`${headerContainerMobileCSS}`}
`;

const OrganizationMembersPage = ({ className }) => {
  const { t } = useTranslation('Users');
  const [inviteUserModalOpened, setInviteUserModalOpened] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState([]);
  const [userToEdit, setUserToEdit] = useState(null);
  const shipperId = useSelector(authSelectors.getUserShipperId);
  const dispatch = useDispatch();

  const handleBack = useCallback(() => {
    dispatch(ordersActions.resetData());
    dispatch(replace(routes.orders.default));
  }, [dispatch]);

  const handleOpenUserModal = useCallback(() => {
    setInviteUserModalOpened(true);
  }, [setInviteUserModalOpened]);

  const onClickUser = (user) => {
    setUserToEdit(user);
  };

  const handleCloseUserModal = useCallback(() => {
    setInviteUserModalOpened(false);
    setUserToEdit(null);
  }, [setInviteUserModalOpened, setUserToEdit]);

  const onCreateUser = useCallback((user) => {
    setUsers((currentUsers) => [user?.data, ...currentUsers]);
  }, []);

  const onUserEdited = useCallback(
    (userUpdated) => {
      setUsers((currentUsers) => {
        const usersUpdated = [...currentUsers];
        const userIndex = usersUpdated.findIndex(
          (user) => user.id === userUpdated.id
        );

        usersUpdated[userIndex] = userUpdated;

        return usersUpdated;
      });
    },
    [setUsers]
  );

  useEffect(() => {
    const getUsers = async () => {
      try {
        setIsLoading(true);
        const res = await api.getUsers({ shipperId });

        if (!!res?.data) {
          setUsers((current) => [...current, ...res?.data]);
        }

        setIsLoading(false);
      } catch (e) {
        console.error(e);
      }
    };

    getUsers();
  }, [shipperId]);

  return (
    <AuthenticatedUserView className={className} handleBack={handleBack}>
      {isLoading && <LoadSpinner size="12" key={0} />}
      {!isLoading && (
        <Main role="main" aria-label="users-page">
          <GridRow>
            <HeaderContainer>
              <Title>{t('title')}</Title>
              <Button
                text={t('new_contact')}
                type="button"
                size="L"
                icon="plus"
                iconPosition="left"
                onClick={handleOpenUserModal}
              />
            </HeaderContainer>
          </GridRow>

          <GridRow>
            <UsersList
              users={users}
              onClickUser={onClickUser}
              shipperId={shipperId}
              t={t}
            />
            {inviteUserModalOpened && (
              <UserModal
                onClose={handleCloseUserModal}
                onCreateUser={onCreateUser}
                shipperId={shipperId}
              />
            )}
            {!!userToEdit && (
              <UserModal
                mode="EDIT"
                onUserEdited={onUserEdited}
                onClose={handleCloseUserModal}
                user={userToEdit}
              />
            )}
          </GridRow>
        </Main>
      )}
    </AuthenticatedUserView>
  );
};

OrganizationMembersPage.propTypes = {
  className: PropTypes.string,
};

OrganizationMembersPage.defaultProps = {
  className: '',
};

export default userIsAuthenticatedRedir(OrganizationMembersPage);
